import { FC, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useQuery } from "@apollo/client";
import type { ScoringType, CloudMaturityBand } from "@/lib/scoring/types";
import { CLOUD_MATURITY_RANGE_QUERY } from "@/apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import type { CloudMaturityRange } from "@/generated/taxonomy";

type Segment = "full" | "new";

const SEGMENT_LABELS: Record<Segment, string> = {
  full: "Full Tech Stack",
  new: "Recently Added Services",
};

function detectSegment(bands: CloudMaturityBand[]): Segment {
  return bands.some((b) => b.newServicesRangeId) ? "new" : "full";
}

function getRangeId(band: CloudMaturityBand, segment: Segment): string {
  return (
    (segment === "new" ? band.newServicesRangeId : band.fullTechStackRangeId) ??
    ""
  );
}

function setRangeId(
  band: CloudMaturityBand,
  segment: Segment,
  rangeId: string,
  displayName: string
): CloudMaturityBand {
  return segment === "new"
    ? { score: band.score, newServicesRangeId: rangeId, displayName }
    : { score: band.score, fullTechStackRangeId: rangeId, displayName };
}

function swapSegment(
  band: CloudMaturityBand,
  toSegment: Segment
): CloudMaturityBand {
  const rangeId = band.fullTechStackRangeId ?? band.newServicesRangeId ?? "";
  return toSegment === "new"
    ? {
        score: band.score,
        newServicesRangeId: rangeId || undefined,
        displayName: band.displayName,
      }
    : {
        score: band.score,
        fullTechStackRangeId: rangeId || undefined,
        displayName: band.displayName,
      };
}

export const initialBand: CloudMaturityBand = { score: 0 };

export const hasErrors = (bands: unknown[]): boolean => {
  const typed = bands as CloudMaturityBand[];
  if (typed.length === 0) return true;
  return typed.some((band, i) => {
    const rangeId = band.fullTechStackRangeId ?? band.newServicesRangeId;
    if (!rangeId || !band.score) return true;
    // Duplicate check within segment
    const others = typed.filter((_, j) => j !== i);
    return others.some(
      (o) =>
        (band.fullTechStackRangeId &&
          o.fullTechStackRangeId === band.fullTechStackRangeId) ||
        (band.newServicesRangeId &&
          o.newServicesRangeId === band.newServicesRangeId)
    );
  });
};

export const getBandSummary = (band: unknown): string => {
  const b = band as CloudMaturityBand;
  const rangeId = b.fullTechStackRangeId ?? b.newServicesRangeId;
  if (!rangeId) return "—";
  const segmentLabel = b.newServicesRangeId
    ? SEGMENT_LABELS.new
    : SEGMENT_LABELS.full;
  const rangeName = b.displayName ?? rangeId;
  return `${segmentLabel}: ${rangeName}`;
};

export const sanitize = (criterion: ScoringType): ScoringType => ({
  ...criterion,
  cloudMaturity: criterion.cloudMaturity?.map(
    ({ displayName: _d, ...rest }) => rest
  ),
});

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const CloudMaturityEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.cloudMaturity ?? [];

  const [segment, setSegment] = useState<Segment>(() => detectSegment(bands));

  const { data, loading } = useQuery<{
    cloudMaturityRange: { data: (CloudMaturityRange | null)[] };
  }>(CLOUD_MATURITY_RANGE_QUERY, { context: { clientName: TAXONOMY_CLIENT } });

  const rangeOptions = useMemo(
    () =>
      loading
        ? []
        : ((data?.cloudMaturityRange?.data ?? []).filter(
            Boolean
          ) as CloudMaturityRange[]),
    [data, loading]
  );

  const handleSegmentChange = (_: unknown, newSegment: Segment | null) => {
    if (!newSegment || newSegment === segment) return;
    setSegment(newSegment);
    onChange({
      ...criterion,
      cloudMaturity: bands.map((b) => swapSegment(b, newSegment)),
    });
  };

  const updateBand = (index: number, patch: Partial<CloudMaturityBand>) => {
    onChange({
      ...criterion,
      cloudMaturity: bands.map((b, i) =>
        i === index ? { ...b, ...patch } : b
      ),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      cloudMaturity: bands.filter((_, i) => i !== index),
    });
  };

  const addBand = () => {
    onChange({ ...criterion, cloudMaturity: [...bands, { score: 0 }] });
  };

  const isDuplicateRange = (
    band: CloudMaturityBand,
    index: number
  ): boolean => {
    const rangeId = getRangeId(band, segment);
    if (!rangeId) return false;
    return bands.some(
      (b, i) => i !== index && getRangeId(b, segment) === rangeId
    );
  };

  return (
    <Box sx={{ px: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={segment}
        onChange={handleSegmentChange}
        sx={{
          mb: 1.5,
          "& .MuiToggleButton-root": { py: 0.5, px: 1.5, fontSize: 12 },
        }}
      >
        <ToggleButton value="full">Full Tech Stack</ToggleButton>
        <ToggleButton value="new">Recently Added Services</ToggleButton>
      </ToggleButtonGroup>

      {bands.map((band, i) => {
        const selectedRangeId = getRangeId(band, segment);
        const duplicate = isDuplicateRange(band, i);
        return (
          <Box
            key={i}
            sx={{
              border: "1px solid",
              borderColor: duplicate ? "error.main" : "divider",
              borderRadius: 1,
              p: 1.5,
              mb: 1,
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Cloud Product Percentage</InputLabel>
              <Select
                label="Cloud Product Percentage"
                value={selectedRangeId}
                onChange={(e) => {
                  const id = e.target.value as string;
                  const option = rangeOptions.find((r) => r.id === id);
                  updateBand(
                    i,
                    setRangeId(band, segment, id, option?.displayName ?? id)
                  );
                }}
              >
                {rangeOptions.map((r) => (
                  <MenuItem key={r.id ?? ""} value={r.id ?? ""}>
                    {r.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {duplicate && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                This percentage range is already used in another band.
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Pts"
                error={band.score === 0}
                value={band.score === 0 ? "" : band.score}
                onChange={(e) =>
                  updateBand(i, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 80 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600, mt: 0.5 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
